import { useState, useEffect } from "react";
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Clock,
  Loader2,
  Award
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { getSalesAgentStats, getOverviewStats } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";

export default function SalesAgentStats() {
  const [agentStats, setAgentStats] = useState([]);
  const [overviewStats, setOverviewStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [agents, overview] = await Promise.all([
        getSalesAgentStats(),
        getOverviewStats()
      ]);
      setAgentStats(agents);
      setOverviewStats(overview);
    } catch (error) {
      console.error("Failed to load stats:", error);
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const chartData = agentStats.map(stat => ({
    name: stat.agent?.split("@")[0] || "Unknown",
    deals: stat.total_deals,
    completed: stat.completed_deals,
    amount: stat.total_funded_amount / 1000, // In thousands
  }));

  const topAgent = agentStats.reduce((max, stat) => 
    stat.total_funded_amount > (max?.total_funded_amount || 0) ? stat : max
  , null);

  return (
    <div data-testid="sales-agent-stats-page">
      <PageHeader
        title="Sales Agent Stats"
        description="Performance analytics for the sales team"
      />

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Deals</p>
                <p className="text-3xl font-bold">{overviewStats?.total_applications || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-3xl font-bold text-emerald-600">{overviewStats?.completed || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Funded</p>
                <p className="text-3xl font-bold">
                  £{(overviewStats?.total_funded_amount || 0).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Agents</p>
                <p className="text-3xl font-bold">{agentStats.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Performer */}
      {topAgent && (
        <Card className="mb-8 border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-amber-200 flex items-center justify-center">
                <Award className="w-8 h-8 text-amber-700" />
              </div>
              <div>
                <p className="text-sm text-amber-700 font-medium">Top Performer</p>
                <p className="text-2xl font-bold text-amber-900">{topAgent.agent}</p>
                <p className="text-sm text-amber-700">
                  £{topAgent.total_funded_amount.toLocaleString()} funded • 
                  {topAgent.completed_deals} completed deals
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Deals by Agent</CardTitle>
            <CardDescription>Total deals vs completed deals per agent</CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="deals" fill="#94a3b8" name="Total Deals" />
                  <Bar dataKey="completed" fill="#059669" name="Completed" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No data available yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agent Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle>Agent Leaderboard</CardTitle>
            <CardDescription>Ranked by total funded amount</CardDescription>
          </CardHeader>
          <CardContent>
            {agentStats.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No agent data available
              </div>
            ) : (
              <div className="space-y-4">
                {agentStats.map((stat, index) => (
                  <div 
                    key={stat.agent} 
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                    data-testid={`agent-stat-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? "bg-amber-200 text-amber-800" :
                        index === 1 ? "bg-slate-200 text-slate-800" :
                        index === 2 ? "bg-orange-200 text-orange-800" :
                        "bg-muted text-muted-foreground"
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">{stat.agent}</p>
                        <p className="text-sm text-muted-foreground">
                          {stat.total_deals} deals • {stat.completion_rate}% completion
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">
                        £{stat.total_funded_amount.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {stat.completed_deals} completed
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
